import type { Contact } from '@hunting-nature/shared-core/interfaces/contact-response.interface';
import { createReducer, on } from '@ngrx/store';

import { GeneralActions } from '../../../shared/redux/general/general.actions';

import { MenuContactsActions } from './menu-contacts.actions';

export interface MenuContactsState {
  contacts: Array<Contact>;
  loading: boolean;
  searchTerm: string;
  contactMenuOpen: boolean;
  selectedContact: Contact | null;
  inviteNewContactPending: boolean;
  inviteNewContactResultModalVisible: boolean;
  resendInvitationLoading: boolean;
  resendInvitationSuccessModalOpen: boolean;
  deleteConfirmationOpen: boolean;
  deleteSuccessOpen: boolean;
}

export const initialMenuContactsState: MenuContactsState = {
  contacts: [],
  loading: false,
  searchTerm: '',
  contactMenuOpen: false,
  selectedContact: null,
  inviteNewContactPending: false,
  inviteNewContactResultModalVisible: false,
  resendInvitationLoading: false,
  resendInvitationSuccessModalOpen: false,
  deleteConfirmationOpen: false,
  deleteSuccessOpen: false,
};

export const menuContactsReducer = createReducer(
  initialMenuContactsState,
  on(MenuContactsActions.contactsOpened, state => ({
    ...state,
    loading: true,
    searchTerm: '',
  })),
  on(MenuContactsActions.filterContacts, (state, { searchTerm }) => ({
    ...state,
    searchTerm,
  })),
  on(MenuContactsActions.contactsLoadSucceeded, (state, { contacts }) => ({
    ...state,
    contacts,
    loading: false,
  })),
  on(MenuContactsActions.goBackToContacts, state => ({
    ...state,
    loading: false,
  })),
  on(MenuContactsActions.contactMenuOpened, (state, { contact }) => ({
    ...state,
    contactMenuOpen: true,
    selectedContact: contact,
  })),
  on(MenuContactsActions.contactMenuClosed, MenuContactsActions.viewUserProfile, state => ({
    ...state,
    contactMenuOpen: false,
    selectedContact: null,
  })),
  on(MenuContactsActions.updateUsername, (state, { contactId, username }) => ({
    ...state,
    contacts: state.contacts.map(c => (c.id === contactId ? { ...c, alias: username } : c)),
    contactMenuOpen: false,
    selectedContact: null,
  })),
  on(MenuContactsActions.updateUsernameFailed, (state, { contactId, originalUsername }) => ({
    ...state,
    contacts: state.contacts.map(c => (c.id === contactId ? { ...c, alias: originalUsername } : c)),
  })),
  on(GeneralActions.goBackToMenuFromContacts, state => ({
    ...state,
    contactMenuOpen: false,
    deleteConfirmationOpen: false,
    deleteSuccessOpen: false,
    selectedContact: null,
  })),
  on(MenuContactsActions.inviteNewContact, state => ({
    ...state,
    inviteNewContactPending: true,
  })),
  on(MenuContactsActions.inviteNewContactSucceeded, state => ({
    ...state,
    inviteNewContactPending: false,
    inviteNewContactResultModalVisible: true,
  })),
  on(MenuContactsActions.inviteNewContactFailed, state => ({
    ...state,
    inviteNewContactPending: false,
  })),
  on(MenuContactsActions.goBackToContacts, state => ({
    ...state,
    inviteNewContactResultModalVisible: false,
  })),
  on(MenuContactsActions.resendInvitation, state => ({
    ...state,
    contactMenuOpen: false,
    selectedContact: null,
    resendInvitationLoading: true,
  })),
  on(MenuContactsActions.resendInvitationSucceeded, state => ({
    ...state,
    resendInvitationLoading: false,
    resendInvitationSuccessModalOpen: true,
  })),
  on(MenuContactsActions.resendInvitationFailed, state => ({
    ...state,
    resendInvitationLoading: false,
  })),
  on(MenuContactsActions.resendInvitationSuccessModalClosed, state => ({
    ...state,
    resendInvitationSuccessModalOpen: false,
  })),
  on(MenuContactsActions.removeUser, state => ({
    ...state,
    deleteConfirmationOpen: true,
  })),
  on(MenuContactsActions.deleteContactCancelled, state => ({
    ...state,
    deleteConfirmationOpen: false,
  })),
  on(MenuContactsActions.deleteContactSucceeded, (state, { contactId }) => ({
    ...state,
    contacts: state.contacts.filter(c => c.id !== contactId),
    deleteConfirmationOpen: false,
    deleteSuccessOpen: true,
  })),
  on(MenuContactsActions.deleteContactFailed, state => ({
    ...state,
    deleteConfirmationOpen: false,
    deleteSuccessOpen: false,
    selectedContact: null,
  })),
  on(MenuContactsActions.deleteContactSuccessClosed, state => ({
    ...state,
    deleteSuccessOpen: false,
    selectedContact: null,
  }))
);
